/**
 * N 域设置**接口契约** —— 用户偏好（`GET/PATCH /api/settings/preferences`）
 *
 * 依据：N 域 PRD **§5.3 N3**（偏好）· **§5.2 N2「提醒偏好」**（策略 H 域 / 存储与 UI N 域）·
 * **§7.2 `AC-N2.20`–`AC-N2.24`** · `DR-020` **§C1**（存储归属）· `DR-031`（提醒偏好唯一载体）·
 * 架构 **§3.4 / §4.2 A 段**（`user_preferences` = **用户级偏好的唯一载体**）。
 *
 * ## 🔴 本文件**不含任何口径** —— 只描述"下发形状"
 *
 * | 判定 | 唯一实现 |
 * |---|---|
 * | 缺行 ⇒ 默认值（三档「常规」+ 五类全开 + 免打扰 `22:00–07:30` 开） | 共享层 `nucleus/reminder.ts` 的 `defaultReminderPrefs()` |
 * | 缺行 ⇒ 主题默认 `'system'`（跟随系统） | 🔴 **DDL 默认值**（`user_preferences.theme`）—— 标量无需内核函数，服务端按 `?? 'system'` 兜底 |
 * | 缺行 ⇒ 时间窗默认 `'3m'`（近 3 个月） | 🔴 `constants` 的 `TxnWindowPolicy.Default`（= PRD §5.2 ⑤ / 原型 select 首项）；⚠️ **DDL 默认 `'1m'` 已废弃** —— 服务端每次 insert 都显式给值，见 `setting.service.ts` |
 * | 免打扰区间合法性 | 同文件的 `assertDoNotDisturbRange()` |
 * | 行 → 内核输入 | 同文件的 `reminderPrefsOf()` |
 * | 🔴 **`'system'` 解析成明/暗** | **端侧**（`client/theme/theme.ts` 的 `resolveThemeMode()`）—— 服务端只存偏好，不解析系统外观 |
 *
 * ## 🔴 作用域 = **用户级**（**已核实三处权威一致**）
 *
 * 架构 §3.4（`user_preferences` = 「**用户级偏好的唯一载体**」）· `DR-031`（提醒偏好唯一载体）·
 * `DR-020` §C1（用户级 = `theme` / `defaultFlowWindow` / **提醒偏好全组**）三处一致。
 *
 * ⚠️ **两处待订正的旧表述**（本批**不改正文**，只登记）：`N域PRD` §5.1 把「提醒偏好」列进
 * **账本级项**那一行、`H域PRD` §5.3 的括注写「作用域 = 账本级」。二者与上述三处权威冲突 ——
 * 按铁律 **1**，订正规格正文需先拍板，故**留待产品侧一句话确认**（见 changelog 的登记段）。
 *
 * ## 🔴 为什么 `reminder` 组是**整体替换**而不是字段级合并
 *
 * 「没传这个字段」与「传了默认值」在 JSON 里**长得一样**（都可能是 `false` / `'normal'`）。
 * 字段级合并会让「用户主动关掉还款提醒」与「这次没提它」无法区分 ⇒ 静默把开关**改回默认**。
 * ⇒ 本接口对 `reminder` 组做**整体替换**：端侧每次都发完整的这一组（它本来就持有完整状态）。
 *
 * 🔴 **`theme` 是标量 ⇒ 字段级**（不适用上面那条）：`'system'` 是一个**真实取值**而不是"没传"，
 * 三态里没有任何一态能代表"没传" ⇒ 只能靠"键在不在"区分，故它**不进** `reminder` 那套整组规则。
 * 两组的更新**互不干扰**：只发 `theme` 不会动提醒偏好，反之亦然（集成测试成对断言）。
 */

import { z } from 'zod';
import { ReminderLevel, ReminderType } from '../enums/plan.js';

// ───────────────────────── 基本校验件 ─────────────────────────

/**
 * 本地时刻 `HH:mm`（`00:00`–`23:59`）。
 *
 * 🔴 **不用 `z.coerce.number()` 之类的宽松解析** —— 免打扰是**账本时区下的本地时刻**
 * （架构 §4.3），格式错一位（`7:30` / `24:00`）就会让判定静默失效。
 */
const ClockSchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, '时刻格式须为 HH:mm（例：22:00 / 07:30）');

/**
 * 流水默认时间窗取值集 —— **近 1 / 3 / 6 个月**（N 域 PRD §5.2 ⑤ · `AC-N2.18`）。
 *
 * 🔴 **与 C 域性能铁律配套**（`AC-N2.19`：流水页**不得默认拉取建账以来全部历史**）——
 * 这个偏好就是"默认拉多长"的旋钮，故它**不是纯装饰性设置**：值越大首屏越慢。
 *
 * 🔴 取值集**刻意只有三档**（可选值 ≠ 页内筛选器的全部档位）：页内的「本月 / 上月 / 今年至今 /
 * 自定义」是**一次性的筛选动作**，不是"我的默认习惯"；把它们也塞进偏好会让默认窗口变成一个
 * 无法预测的东西（"默认上月"到底指哪个上月）。**映射到页内档位**由端侧 `windowForPreference()` 承担。
 */
export const TxnWindowValues = ['1m', '3m', '6m'] as const;
export type TxnWindowValue = (typeof TxnWindowValues)[number];

// ───────────────────────── 主题（外观） ─────────────────────────

/**
 * 主题三态 —— **默认「跟随系统」**（`AC-N3.1` · N 域 PRD §5.3 `N3`「主题」行）。
 *
 * 🔴 顺序**不是**渲染顺序（原型 `08-家庭与设置_原型.html` 的 segmented 顺序 = 浅色 / 深色 / 跟随系统）——
 * 契约只声明**取值集**，展示顺序归端侧文案层（`features/theme-labels.ts`）。
 *
 * 🔴 `system` 是**真实取值**而非"未设置"：用户**显式**选了「跟随系统」也要能被记住，
 * 否则下次系统变暗时无法判断"该不该跟"。
 */
export const ThemeValues = ['system', 'light', 'dark'] as const;
export type ThemeValue = (typeof ThemeValues)[number];

// ───────────────────────── 提醒偏好 ─────────────────────────

/**
 * 提醒偏好（**用户级**·三档 + 五类开关 + 免打扰）。
 *
 * 🔴 **形状与内核输入 `ReminderPrefsInput` 逐字段一致** —— 这样「契约 → 内核」**零转换**
 * （转换函数本身就是"第二份实现"的温床）。
 */
export const ReminderPreferenceSchema = z.object({
  /** 三档总开关（`key_only` / `normal` / `all`；默认 `normal`） */
  level: z.enum([ReminderLevel.KeyOnly, ReminderLevel.Normal, ReminderLevel.All]),
  /** 五类按类型开关（默认全开） */
  typeEnabled: z.object({
    [ReminderType.Repayment]: z.boolean(),
    [ReminderType.Billing]: z.boolean(),
    [ReminderType.Budget]: z.boolean(),
    [ReminderType.Goal]: z.boolean(),
    [ReminderType.Habit]: z.boolean(),
  }),
  /** 免打扰开关（默认开） */
  dndEnabled: z.boolean(),
  /** 免打扰起点（**账本时区下的本地时刻**） */
  dndStart: ClockSchema,
  /** 免打扰终点（可跨零点，如 `07:30`） */
  dndEnd: ClockSchema,
});
export type ReminderPreferenceView = z.infer<typeof ReminderPreferenceSchema>;

// ───────────────────────── 请求 / 响应 ─────────────────────────

/**
 * `PATCH /api/settings/preferences` 请求体。
 *
 * 🔴 **`reminder` 组整体替换**（理由见文件头）。🔴 **空 body（`{}`）⇒ 无操作**
 * （不是"重置为默认" —— 空请求不该有副作用）。
 */
export const UpdatePreferencesRequestSchema = z.object({
  reminder: ReminderPreferenceSchema.optional(),
  /**
   * 主题三态（**标量 · 字段级** —— 见文件头；`'system'` 是真实取值，不是"没传"）。
   * 缺省 = 不改主题。
   */
  theme: z.enum(ThemeValues).optional(),
  /**
   * 流水默认时间窗（**标量 · 字段级**）。缺省 = 不改。
   * 缺行 / 未设置 ⇒ 默认 **`'3m'`**（`TxnWindowPolicy.Default`）。
   */
  txnWindow: z.enum(TxnWindowValues).optional(),
});
export type UpdatePreferencesRequest = z.infer<typeof UpdatePreferencesRequestSchema>;

/** `GET /api/settings/preferences` 响应 */
export interface PreferencesView {
  /** 提醒偏好（**用户级** · 唯一载体 = `user_preferences` · `DR-031`） */
  reminder: ReminderPreferenceView;
  /**
   * 主题三态（**用户级** · 唯一载体 = `user_preferences.theme` · `DR-020` §C1）。
   * 无行 ⇒ `'system'`（跟随系统）—— 与 DDL 默认值一致。
   */
  theme: ThemeValue;
  /**
   * 流水默认时间窗（**用户级** · `user_preferences.default_txn_window`）。
   * 无行 ⇒ `'3m'`（近 3 个月 · `TxnWindowPolicy.Default`）。
   */
  txnWindow: TxnWindowValue;
  /**
   * 账本时区（`Asia/Shanghai`）—— 免打扰是**该时区下的本地时刻**。
   * 端侧据此在面板上标注「按账本时区」，避免用户以为它按手机时区。
   */
  timezone: string;
}
